import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { errorMessage } from '../../utils/staffDocuments';
import { formatDate } from '../../utils/dates';
import { Skeleton, InlineError, useToast } from '../ui';
import './staffDocuments.css';

// Club admins: who has signed this version, and the switch that requires it
export default function Signatures({ doc, ownSigned, online, onRequiredChange }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const mustSignFirst = doc.signerRoles.includes('CLUB_ADMIN') && !ownSigned;

  const load = useCallback(() => {
    setLoadError(null);
    axios.get(`/api/staff-documents/${doc.id}/signatures`)
      .then(res => setData(res.data))
      .catch(err => setLoadError(errorMessage(err, "Couldn't load who has signed.")));
  }, [doc.id]);
  useEffect(() => { load(); }, [load, doc.version, ownSigned]);

  // A switch: saves as soon as it's flipped
  const setRequired = async (required) => {
    setSaving(true);
    setError(null);
    try {
      await axios.put(`/api/staff-documents/${doc.id}/required`, { required });
      setData(d => ({ ...d, required }));
      onRequiredChange(required);
      toast(required ? 'Staff must now sign it' : 'Signing is no longer required');
    } catch (err) {
      setError(errorMessage(err, "Couldn't save. Try again."));
    } finally {
      setSaving(false);
    }
  };

  const waiting = data?.staff.filter(s => !s.signed).length ?? 0;
  return (
    <section className="bk-card coc-admin">
      <h3>Who has signed</h3>
      {loadError && <InlineError onRetry={load}>{loadError}</InlineError>}
      {!data && !loadError && <Skeleton rows={2} label="Loading who has signed…" />}
      {data && (
        <>
          <p>{waiting === 0 ? 'Everyone has signed this version.' : `${waiting} of ${data.staff.length} still to sign.`}</p>
          <ul className="coc-staff">
            {data.staff.map(s => (
              <li key={s.id}>
                <span>{s.firstName} {s.lastName}{s.role === 'CLUB_ADMIN' ? ' (admin)' : s.role === 'WELFARE' ? ' (welfare)' : ''}</span>
                <span className={`bk-pill bk-pill--${s.signed ? 'good' : 'bad'}`}>
                  {s.signed ? `Signed ${formatDate(s.signed.signedAt)}` : 'Not signed'}
                </span>
              </li>
            ))}
          </ul>
          {!doc.archived && (
            <>
              <label className="sd-switch">
                <input
                  type="checkbox"
                  role="switch"
                  className="bk-switch"
                  checked={data.required}
                  disabled={saving || !online || (!data.required && mustSignFirst)}
                  onChange={e => setRequired(e.target.checked)}
                />
                Staff must sign before using staff pages
              </label>
              <p className="bk-small sd-switch__hint">
                {!data.required && mustSignFirst
                  ? 'Sign it yourself first, so you can always get back in.'
                  : "Anyone who hasn't signed this version is taken to Staff documents instead of staff pages. They can still use the home page and their own account."}
              </p>
              {error && <p className="bk-form-error" role="alert">{error}</p>}
            </>
          )}
        </>
      )}
    </section>
  );
}
